import { brandsOpenApi } from '@constants';
import { axiosBoa } from '@utils';
import { getParent, types } from 'mobx-state-tree';
import { models } from './constants';
import { createStoreAction } from './utils';

export const ProductsStore = types
    .model('ProductsStore', {
        products: types.array(models.ProductModel),
        productTypes: types.array(
            types.model({
                id: types.number,
                title: types.string
            })
        ),
        productCollections: types.array(
            types.model({
                id: types.number,
                title: types.string
            })
        ),
        meta: types.maybeNull(
            types.model({
                page: types.maybeNull(types.number),
                perPage: types.maybeNull(types.number),
                total: types.maybeNull(types.number),
                totalPages: types.maybeNull(types.number)
            })
        ),
        filters: types.model({
            klickly_statuses: types.maybe(types.array(types.number)),
            ecommerce_statuses: types.maybe(types.array(types.number)),
            typeIds: types.maybe(types.array(types.number)),
            search: types.maybe(types.string)
        }),
        sorters: types.model({
            sort: 'title',
            order: 'asc'
        }),
        pending: false,
        bulkUploadPending: false
    })
    .views((self) => ({
        get root() {
            return getParent(self);
        }
    }))
    .actions((self) => {
        const action = createStoreAction(self);

        return {
            setPending(pending) {
                self.pending = pending;
            },
            resetFilters() {
                self.filters = {};
            },
            updateFilters(filters) {
                Object.assign(self.filters, filters);
            },
            resetSorters() {
                self.sorters = {
                    sort: 'title',
                    order: 'asc'
                };
            },
            updateSorters(sorters) {
                Object.assign(self.sorters, sorters);
            },
            getProduct(productId) {
                return self.products.find((product) => product.id === productId);
            },
            getProducts: action(
                function* getProducts(params = {}) {
                    self.pending = true;
                    Object.assign(params, self.filters, self.sorters);

                    const data = yield axiosBoa.get(brandsOpenApi.products.list, { params });
                    self.products = data.data;
                    self.meta = data.meta;
                },
                { onFinally: () => (self.pending = false) }
            ),
            getProductVariants: action(
                function* getProductVariants(productId, page = 1) {
                    const product = self.getProduct(productId);
                    product.variants.setLoading(true);
                    const response = yield axiosBoa.get(brandsOpenApi.productVariants.list(productId), {
                        params: {
                            page,
                            limit: 10
                        }
                    });
                    const updatedProduct = self.getProduct(productId);
                    if (updatedProduct) {
                        updatedProduct.variants.updateFromResponse(response);
                        updatedProduct.variants.setLoading(false);
                    }
                },
                { onFinally: () => (self.pending = false) }
            ),
            getProductTypes: action(
                function* getProductTypes(params = {}) {
                    self.typesPending = true;

                    const result = yield axiosBoa.get(brandsOpenApi.productTypes.list, { params });
                    self.productTypes = result.data;
                },
                { onFinally: () => (self.typesPending = false) }
            ),
            getProductCollections: action(
                function* getProductCollections(params = {}) {
                    self.collectionsPending = true;

                    const result = yield axiosBoa.get(brandsOpenApi.productCollections.list, { params });
                    self.productCollections = result.data;
                },
                { onFinally: () => (self.collectionsPending = false) }
            ),
            bulkUpload: action(
                function* bulkUpload(data) {
                    self.bulkUploadPending = true;

                    return yield axiosBoa.post(brandsOpenApi.bulkUpload.products, data, {
                        headers: {
                            'Content-Type': 'multipart/form-data'
                        }
                    });
                },
                { onFinally: () => (self.bulkUploadPending = false) }
            ),
            createDemoProducts: action(function* createDemoProducts() {
                return yield axiosBoa.post(brandsOpenApi.setup.demoProducts);
            })
        };
    });
